import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ChevronRight, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import {
  STATIC_MERGE_FIELD_CATEGORIES,
  fetchMergeFieldCategories,
  getCategoryByKey,
  searchMergeFields,
} from '../../utils/automationMergeFields';
import { computePickerPosition } from '../../utils/customValuesPickerPosition';

const CustomValuesPicker = ({
  open,
  onClose,
  onInsert,
  anchorRef,
  token,
}) => {
  const [categories, setCategories] = useState(STATIC_MERGE_FIELD_CATEGORIES);
  const [activeCategoryKey, setActiveCategoryKey] = useState(null);
  const [activeChildKey, setActiveChildKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 520,
    maxHeight: 560,
    placement: 'below',
  });
  const searchRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setCategories(STATIC_MERGE_FIELD_CATEGORIES);
    fetchMergeFieldCategories(token).then((data) => {
      if (mounted) setCategories(data);
    });
    return () => { mounted = false; };
  }, [open, token]);

  useEffect(() => {
    if (!open) {
      setActiveCategoryKey(null);
      setActiveChildKey(null);
      setSearchQuery('');
      setHighlightIndex(0);
    }
  }, [open]);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPosition(computePickerPosition(rect));
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (activeCategoryKey && !searchQuery) {
          if (activeChildKey) {
            setActiveChildKey(null);
          } else {
            setActiveCategoryKey(null);
          }
          return;
        }
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef, activeCategoryKey, activeChildKey, searchQuery]);

  const searchResults = useMemo(
    () => searchMergeFields(searchQuery, categories),
    [searchQuery, categories]
  );

  const activeCategory = activeCategoryKey ? getCategoryByKey(activeCategoryKey, categories) : null;
  const activeChild = activeCategory?.children?.find((c) => c.key === activeChildKey) || null;

  const listItems = useMemo(() => {
    if (searchQuery.trim()) {
      return searchResults.map((r) => ({
        type: 'field',
        label: r.label,
        value: r.value,
        breadcrumb: r.breadcrumb,
      }));
    }
    if (activeChild) {
      return (activeChild.fields || []).map((f) => ({ type: 'field', ...f }));
    }
    if (activeCategory) {
      const items = [];
      (activeCategory.fields || []).forEach((f) => items.push({ type: 'field', ...f }));
      (activeCategory.children || []).forEach((child) => {
        items.push({ type: 'child', key: child.key, label: child.label });
      });
      return items;
    }
    return categories.map((c) => ({
      type: 'category',
      key: c.key,
      label: c.label,
      hasChildren: Boolean((c.fields || []).length || (c.children || []).length),
    }));
  }, [searchQuery, searchResults, activeCategory, activeChild, categories]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [searchQuery, activeCategoryKey, activeChildKey]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, listItems.length, activeCategoryKey, activeChildKey, searchQuery, updatePosition]);

  const handleSelect = (item) => {
    if (item.type === 'category') {
      setActiveCategoryKey(item.key);
      setActiveChildKey(null);
      setSearchQuery('');
      return;
    }
    if (item.type === 'child') {
      setActiveChildKey(item.key);
      setSearchQuery('');
      return;
    }
    if (item.type === 'field' && item.value) {
      onInsert?.(item.value);
      onClose?.();
    }
  };

  const handleKeyDownList = (e) => {
    if (!listItems.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, listItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = listItems[highlightIndex];
      if (item) handleSelect(item);
    } else if (e.key === 'Backspace' && !searchQuery && activeCategoryKey) {
      if (activeChildKey) setActiveChildKey(null);
      else setActiveCategoryKey(null);
    }
  };

  if (!open || typeof document === 'undefined') return null;

  const breadcrumbLabel = activeChild
    ? activeCategory?.label
    : activeCategory?.label;

  return createPortal(
    <div
      ref={panelRef}
      data-testid="custom-values-picker"
      data-placement={position.placement}
      className="fixed z-[9999] flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: position.maxHeight,
      }}
    >
      <div className="border-b border-slate-200 bg-white p-3">
        {activeCategoryKey && !searchQuery ? (
          <button
            type="button"
            className="mb-2 flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
            onClick={() => {
              if (activeChildKey) setActiveChildKey(null);
              else setActiveCategoryKey(null);
            }}
            data-testid="custom-values-back"
          >
            <ArrowLeft size={14} />
            <span>Custom values</span>
            <span className="text-slate-400">&gt;</span>
            <span className="font-semibold text-slate-900">{breadcrumbLabel}</span>
            {activeChild && (
              <>
                <span className="text-slate-400">&gt;</span>
                <span className="font-semibold text-slate-900">{activeChild.label}</span>
              </>
            )}
          </button>
        ) : (
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Custom values</h4>
        )}
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            ref={searchRef}
            data-testid="custom-values-search"
            placeholder="Type to search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDownList}
            className="h-9 pl-8 text-sm border-slate-200 focus-visible:ring-blue-500/20"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-1" data-testid="custom-values-list">
        {listItems.length === 0 && (
          <p className="px-3 py-4 text-sm text-slate-500">No matching fields</p>
        )}
        {listItems.map((item, index) => (
          <button
            key={`${item.type}-${item.key || item.value || item.label}-${index}`}
            type="button"
            data-testid={
              item.type === 'category'
                ? `custom-values-category-${item.key}`
                : item.type === 'field'
                  ? `custom-values-field-${item.value}`
                  : `custom-values-child-${item.key}`
            }
            onClick={() => handleSelect(item)}
            className={cn(
              'flex w-full items-center justify-between px-3 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50',
              highlightIndex === index && 'bg-blue-50'
            )}
          >
            <span className="min-w-0 flex-1 truncate">
              {item.breadcrumb || item.label}
            </span>
            {(item.type === 'category' || item.type === 'child') && (
              <ChevronRight size={14} className="ml-2 shrink-0 text-slate-400" />
            )}
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default CustomValuesPicker;
